import { useCallback, useState } from 'react'
import { api } from '../api/client.js'

export interface SendMessageResponse {
  messageId: string
  /** Present on a normal (non-pending) send. */
  isFirstResponse?: boolean
  /**
   * UI-1e Task 16: when automation flags the outbound message, the backend
   * responds 201 with status:'pending_approval' + approvalId instead of the
   * normal `isFirstResponse` shape.
   */
  status?: 'pending_approval'
  approvalId?: string
  /** PC-08: a card number was found and masked before the message was stored or sent. */
  cardDataMasked?: boolean
  /** The text as stored and sent, when it was masked. */
  maskedText?: string
}

export interface SendMessageTemplate {
  name: string
  languageCode: string
  variables: string[]
}

export interface UseSendMessageReturn {
  send: (
    conversationId: string,
    contentText: string,
    attachments?: string[],
    template?: SendMessageTemplate,
  ) => Promise<SendMessageResponse>
  isPending: boolean
}

export function useSendMessage(): UseSendMessageReturn {
  const [isPending, setPending] = useState(false)
  const send = useCallback(
    async (
      conversationId: string,
      contentText: string,
      attachments?: string[],
      template?: SendMessageTemplate,
    ) => {
      setPending(true)
      try {
        return await api.post<SendMessageResponse>(
          `/conversations/${conversationId}/messages`,
          {
            contentText,
            visibility: 'customer',
            ...(attachments?.length ? { attachments } : {}),
            ...(template ? { template } : {}),
          },
        )
      } finally {
        setPending(false)
      }
    },
    [],
  )
  return { send, isPending }
}
